import { client } from "./api";

export const apiRoutes = {
  getAllApplications: "applications",
  createApplication: "applications",
  createBlankApplication: "applications/new",
  getApplication: (applicationId) => `applications/${applicationId}`,
  // SOUND-649: link any two records. Symmetric read — ask about an entity and
  // get everything attached, whichever end of the edge it sits on.
  entityLinks: "entity-links",
  updateApplication: (applicationId) => `applications/${applicationId}`,
  // SOUND-649: owners are a collection, so a single one is addressable. The
  // section PUT would wholesale-replace the array and delete the others.
  addApplicationOwner: (applicationId) => `applications/${applicationId}/owners`,
  updateApplicationOwner: (applicationId, ownerId) =>
    `applications/${applicationId}/owners/${ownerId}`,
  deleteApplicationOwner: (applicationId, ownerId) =>
    `applications/${applicationId}/owners/${ownerId}`,
  uploadApplicationFile: (applicationId) =>
    `applications/${applicationId}/upload`,
  deleteApplicationFile: (applicationId) =>
    `applications/${applicationId}/file`,
  downloadApplicationFile: (applicationId) =>
    `applications/${applicationId}/signed-url`,
  downloadAllApplicationFiles: (applicationId) =>
    `applications/${applicationId}/download-zip`,
  submitApplication: (applicationId) => `applications/${applicationId}/submit`,
  deleteApplication: (applicationId) => `applications/${applicationId}`,
  getApplicationCompleteness: (applicationId) =>
    `applications/${applicationId}/completeness`,
  appendApplicationDba: (applicationId) =>
    `applications/${applicationId}/dbas`,
  updateApplicationDba: (applicationId, dbaId) =>
    `applications/${applicationId}/dbas/${dbaId}`,

  // Analysis & Analytics Routes
  binAnalysis: "analytics/bin-analysis",

  // Daily Performance analytics (SOUND-409) — internal, over presentation mart
  analyticsDailyPerformance: "analytics/daily-performance",
  analyticsDailyPerformanceTimeseries: "analytics/daily-performance/timeseries",
  analyticsDailyPerformanceSummary: "analytics/daily-performance/summary",
  analyticsDailyPerformanceFilters: "analytics/daily-performance/filters",

  // Authorization Performance analytics (SOUND-585 epic) — internal, over
  // presentation.mart_auth_performance. Two measures only (attempts, approved);
  // auth_rate is recomputed, never stored. /report takes a comma-separated
  // groupBy of whitelisted dimensions and returns { groupBy, rowCount, rows }.
  analyticsAuthPerformance: "analytics/auth-performance",
  analyticsAuthPerformanceTimeseries: "analytics/auth-performance/timeseries",
  analyticsAuthPerformanceSummary: "analytics/auth-performance/summary",
  analyticsAuthPerformanceFilters: "analytics/auth-performance/filters",
  analyticsAuthPerformanceReport: "analytics/auth-performance/report",

  // Rerunner Recovery analytics (SOUND-413) — internal, over presentation mart
  analyticsRerunner: "analytics/rerunner-summary",
  analyticsRerunnerTimeseries: "analytics/rerunner-summary/timeseries",
  analyticsRerunnerSummary: "analytics/rerunner-summary/summary",
  analyticsRerunnerFilters: "analytics/rerunner-summary/filters",

  // LTV v2 (SOUND-810/811) — the decomposed model over MART_LTV_COHORT_V2,
  // computed client-side by src/lib/ltvDecomposed.js. A 404 with
  // { available: false } means the v2 mart isn't synced in this environment.
  //   /ltv/v2/filters[?company=] → { companies, products, offerFamilies, currencies }
  //   /ltv/v2/cohorts?company=&(product=|family=)[&currency=] → { data: [...], client: [...] }
  ltvV2Filters: (company) =>
    `ltv/v2/filters${company ? `?company=${encodeURIComponent(company)}` : ""}`,
  ltvV2Cohorts: ({ company, product, family, currency } = {}) => {
    const p = new URLSearchParams();
    if (company) p.set("company", company);
    if (product) p.set("product", product);
    else if (family) p.set("family", family);
    if (currency) p.set("currency", currency);
    const qs = p.toString();
    return `ltv/v2/cohorts${qs ? `?${qs}` : ""}`;
  },

  // Risk model (SOUND-472) — internal, DEV, over the rebuilt presentation.mart_risk
  // (VAMP + ECM/HECM per sound_mid_id × month). Ratios are BPS, standings precomputed.
  riskMids: "risk/mids",
  riskUnresolved: "risk/unresolved",
  riskSummary: "risk/summary",
  riskMidTrend: (soundMidId) => `risk/mids/${encodeURIComponent(soundMidId)}/trend`,
  riskFilters: "risk/filters",

  // Audit Log Routes:
  createAuditLog: `/audit-log`,
  getAuditLogs: `/audit-log`,
  getAuditLogByID: (id) => `/audit-log/${id}`,

  // Rule Routes:
  getRules: (query = {}) => {
    const params = new URLSearchParams(query).toString();
    return `/rules${params ? `?${params}` : ""}`;
  },
  getRuleByID: (ruleId) => `/rules/${ruleId}`,
  createRule: "/rules",
  editRule: (ruleId) => `/rules/${ruleId}`,
  rollbackRule: (ruleId) => `/rules/${ruleId}/rollback`,
  bulkCreateRules: "/rules/bulk",
  getRuleConstraints: "/rules/constraints",


  // MID Application Routes:
  listMidApplications: "mid-applications",
  // SOUND-689/690 — the cross-application pend list and its vocabulary.
  listPends: "pends",
  pendCategories: "pends/categories",
  getMidApplicationById: (applicationId) =>
    `mid-applications/${applicationId}`,
  createMidApplication: "mid-applications",
  getMerchants: "/mid-applications/merchants",
  addMidApplicationComment: (applicationId) =>
    `mid-applications/${applicationId}/comments`,
  addMidApplicationPend: (applicationId) =>
    `mid-applications/${applicationId}/pends`,
  resolveMidApplicationPend: (applicationId, pendId) =>
    `mid-applications/${applicationId}/pends/${pendId}/resolve`,
  // SOUND-619 — bulk resolve. Distinct from the single-pend route above by
  // segment count, so the two do not collide server-side.
  resolveMidApplicationPends: (applicationId) =>
    `mid-applications/${applicationId}/pends/resolve`,
  changeApplicationStatus: (companyId) => `applications/${companyId}/status`,
  changeMidApplicationStatus: (applicationId) =>
    `mid-applications/${applicationId}/status`,

  // SOUND-776 — Kurv (Stratus) transmit, internal-only. DRY RUN unless
  // `live` is true; :applicationId is the MID application's id. The MCC search
  // returns Stratus row ids — `id` is what `mccId` wants, NOT the 4-digit code.
  stratusTransmit: (applicationId, { live = false } = {}) =>
    `integrations/stratus/transmit/${encodeURIComponent(applicationId)}${live ? "?live=true" : ""}`,
  stratusMccSearch: (search) =>
    `integrations/stratus/mcc?search=${encodeURIComponent(search || "")}`,

  // MID Manager (Portfolio) Routes:
  listMids: "/mids",
  getMidById: (midId) => `/mids/${midId}`,
  createMid: "/mids",
  updateMid: (midId) => `/mids/${midId}`,
  changeMidStatus: (midId) => `/mids/${midId}/status`,
  getMidIntegrations: (midId) => `/mids/${midId}/integrations`,
  addMidIntegration: (midId) => `/mids/${midId}/integrations`,
  // SOUND-652: bulk MID import into ONE company (internal-only in Phase 1).
  // :companyId is the company imported INTO; every DBA and duplicate check is
  // scoped to it server-side. Contract: docs/plans/2026-10-30-sound-652-bulk-mid-import.md
  midImportMatch: (companyId) => `/companies/${companyId}/mid-imports/match`,
  midImportPreview: (companyId) => `/companies/${companyId}/mid-imports/preview`,
  midImportCommit: (companyId) => `/companies/${companyId}/mid-imports`,
  // Keyed by the integration's own _id, not a MID + array index —
  // midIntegrations is its own collection (canonical schema). CRM-only soft
  // deactivate (status -> inactive); does NOT touch the provider.
  unlinkMidIntegration: (integrationId) => `/mid-integrations/${integrationId}`,

  // User Routes:
  getUserMe: "/users/me",
  // SOUND-715: the BRANDED confirmation email, sent by us rather than by the
  // Firebase client SDK, which delivers its own unbranded default.
  sendVerification: "/users/send-verification",
  createUserProfile: "/users",
  updateUserProfile: (uid) => `/users/${uid}`,
  getCompanyUsers: "/users",
  getUser: (uid) => `/users/${uid}`,
  deactivateUser: (uid) => `/users/${uid}/deactivate`,
  // SOUND-248: admin-triggered reset. The link is emailed to the user and
  // never returned here, so an admin cannot capture another person's token.
  sendUserPasswordReset: (uid) => `/users/${uid}/password-reset`,
  reactivateUser: (uid) => `/users/${uid}/reactivate`,
  getUsersByCompanyId: `/users`,
  inviteUser: `/users/invite`,

  // Job Functions (SOUND-503/504) — internal-only ops-queue vocabulary +
  // per-user assignment. `?include=holders` enriches each function with its
  // holders + resolved default owner.
  listFunctions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/functions${qs ? `?${qs}` : ""}`;
  },
  createFunction: "/functions",
  updateFunction: (functionKey) => `/functions/${encodeURIComponent(functionKey)}`,
  getFunctionUsers: (functionKey) =>
    `/functions/${encodeURIComponent(functionKey)}/users`,
  setUserFunctions: (uid) => `/users/${uid}/functions`,

  // Workload / resource management (SOUND-538 / backend SOUND-537) — internal-only
  // aggregate: open ticket load + SLA breach-risk per function/queue + per
  // assignee + unassigned. Powers the /settings/workload dashboard.
  getWorkload: "/workload",

  // Ticket Workflow Templates (SOUND-505/506) — internal-only CRUD. Create/edit
  // are drafts; /activate bumps version and retires the prior live version of
  // the SAME variant only (SOUND-785). /variants lists a type's live variants,
  // default first; /make-default moves the type's default atomically.
  listTicketTemplates: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/ticket-templates${qs ? `?${qs}` : ""}`;
  },
  getTicketTemplate: (templateId) => `/ticket-templates/${templateId}`,
  createTicketTemplate: "/ticket-templates",
  updateTicketTemplate: (templateId) => `/ticket-templates/${templateId}`,
  activateTicketTemplate: (templateId) => `/ticket-templates/${templateId}/activate`,
  deactivateTicketTemplate: (templateId) => `/ticket-templates/${templateId}/deactivate`,
  deleteTicketTemplate: (templateId) => `/ticket-templates/${templateId}`,
  // Static path segment — the API registers it before `/:templateId`.
  listTicketTemplateVariants: (requestType) =>
    `/ticket-templates/variants?${new URLSearchParams({ requestType }).toString()}`,
  makeDefaultTicketTemplate: (templateId) => `/ticket-templates/${templateId}/make-default`,

  // Request types (SOUND-544) — internal-only authoring over the vocabulary.
  // `key` is create-only; deactivate/activate are separate endpoints (PUT
  // refuses `active`). Deactivate may 409 with `dependants`.
  listTicketRequestTypesAdmin: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/ticket-request-types${qs ? `?${qs}` : ""}`;
  },
  getTicketRequestTypeAdmin: (key) => `/ticket-request-types/${encodeURIComponent(key)}`,
  createTicketRequestType: "/ticket-request-types",
  updateTicketRequestType: (key) => `/ticket-request-types/${encodeURIComponent(key)}`,
  deactivateTicketRequestType: (key) => `/ticket-request-types/${encodeURIComponent(key)}/deactivate`,
  activateTicketRequestType: (key) => `/ticket-request-types/${encodeURIComponent(key)}/activate`,

  // Invitation Routes
  getInvitations: "/invitations",
  createInvitation: "/invitations",
  cancelInvitation: (id) => `/invitations/${id}`,
  validateInvitation: (token) => `/invitations/validate/${token}`,

  // Company Routes
  getCompanies: "companies",
  getCompany: (companyId) => `companies/${companyId}`,
  createCompany: "companies",
  updateCompany: (companyId) => `companies/${companyId}`,
  getCompanyChildren: (companyId) => `companies/${companyId}/children`,
  getCompanyPerformance: (companyId, range = 30) =>
    `companies/${companyId}/performance?range=${range}`,
  // SOUND-294: Retroactive source attribution CRUD
  addCompanySource: (companyId) => `companies/${companyId}/sources`,
  updateCompanySource: (companyId, partnerId) =>
    `companies/${companyId}/sources/${partnerId}`,
  removeCompanySource: (companyId, partnerId) =>
    `companies/${companyId}/sources/${partnerId}`,

  // API Key Routes
  getApiKeys: "/api-keys",
  createApiKey: "/api-keys",
  revokeApiKey: (id) => `/api-keys/${id}`,

  // Webhook Routes (session auth)
  getWebhooks: "/webhooks",
  createWebhook: "/webhooks",
  updateWebhook: (id) => `/webhooks/${id}`,
  disableWebhook: (id) => `/webhooks/${id}`,
  getWebhookDeliveries: (id) => `/webhooks/${id}/deliveries`,

  // Application Schema
  getApplicationSchema: "/application-schema",

  // Processor Routes
  getProcessors: "/processors",
  getProcessor: (id) => `/processors/${id}`,
  createProcessor: "/processors",
  updateProcessor: (id) => `/processors/${id}`,
  deleteProcessor: (id) => `/processors/${id}`,
  getProcessorMatches: (companyId) => `/processors/match/${companyId}`,

  // Agent + Bank registries (SOUND-672 / backend SOUND-671) — internal-only
  // GLOBAL config CRUD, the two counterparties either side of a processor in
  // the AGENT -> PROCESSOR -> SPONSOR BANK chain. Minimal by design (name,
  // status, notes, tags) — NOT a copy of the 24-field processor doc.
  //
  // "Agent", never "ISO": an agent is an ISO for a processor and a processor is
  // itself an ISO for its banks, so the term is ambiguous in both directions.
  //
  // List defaults to active only; pass { includeInactive: "true" } for all.
  // DELETE soft-deactivates (status: "inactive") — there is no hard delete.
  listAgents: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/agents${qs ? `?${qs}` : ""}`;
  },
  getAgent: (agentId) => `/agents/${agentId}`,
  createAgent: "/agents",
  updateAgent: (agentId) => `/agents/${agentId}`,
  deleteAgent: (agentId) => `/agents/${agentId}`,

  listBanks: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/banks${qs ? `?${qs}` : ""}`;
  },
  getBank: (bankId) => `/banks/${bankId}`,
  createBank: "/banks",
  updateBank: (bankId) => `/banks/${bankId}`,
  deleteBank: (bankId) => `/banks/${bankId}`,

  // Disputifier Routes
  disputifierListMerchants: "/disputifier/merchants",
  disputifierEnrollMerchant: "/disputifier/merchants",
  disputifierGetMerchant: (dbaId) => `/disputifier/merchants/${dbaId}`,
  disputifierUpdateMerchant: (dbaId) => `/disputifier/merchants/${dbaId}`,
  disputifierEnrollMid: (dbaId) => `/disputifier/merchants/${dbaId}/mids`,
  disputifierUpdateMid: (midId) => `/disputifier/mids/${midId}`,
  disputifierCloseMid: (midId) => `/disputifier/mids/${midId}/close`,
  disputifierGetEnrollments: (midId) => `/disputifier/mids/${midId}/enrollments`,
  disputifierSyncMid: (midId) => `/disputifier/mids/${midId}/sync`,
  // Link-existing (SOUND-458): live Disputifier merchant/MID picker
  disputifierLinkMerchants: (search) =>
    `/disputifier/link-merchants${search ? `?search=${encodeURIComponent(search)}` : ""}`,
  disputifierLinkCandidates: (merchantId) =>
    `/disputifier/link-candidates?merchantId=${encodeURIComponent(merchantId)}`,
  disputifierLink: "/disputifier/link",

  // NMI Routes
  nmiProvision: "/nmi/provision",
  nmiGetStatus: (midId) => `/nmi/status/${midId}`,
  nmiSync: (midId) => `/nmi/sync/${midId}`,
  nmiGateways: (credentialId) => `/nmi/gateways?credentialId=${encodeURIComponent(credentialId)}`,
  nmiLinkCandidates: (credentialId, merchantId) =>
    `/nmi/link-candidates?credentialId=${encodeURIComponent(credentialId)}&merchantId=${encodeURIComponent(merchantId)}`,
  nmiLink: "/nmi/link",

  // Checkout Champ Routes
  checkoutchampProvision: "/checkoutchamp/provision",
  checkoutchampStatus: (midId) => `/checkoutchamp/status/${midId}`,
  checkoutchampUpdate: (midId) => `/checkoutchamp/update/${midId}`,
  // No builder for the provider force-close endpoints (POST /nmi/deactivate/:midId,
  // POST /checkoutchamp/deactivate/:midId). They stay live on the API for real
  // force-close work, but nothing in the UI should reach them casually — see the
  // note above handleUnlinkIntegration in pages/mids/MidDetail.jsx.

  // File Management Routes
  listFiles: "/files",
  // SOUND-661: the category vocabulary is SERVER-owned. FilesTab used to
  // hardcode 8 of the 157 values present in prod, so 41.7% of files could not
  // be filtered to. Same rule as ticketRequestTypes — never hardcode this list.
  fileCategories: "/files/categories",
  uploadFiles: "/files/upload",
  getFile: (fileId) => `/files/${fileId}`,
  downloadFile: (fileId) => `/files/${fileId}/download`,
  updateFile: (fileId) => `/files/${fileId}`,
  deleteFile: (fileId) => `/files/${fileId}`,
  downloadFilesZip: "/files/download-zip",

  contactUs: "/contact",

  // Feedback (Bug Nub)
  submitFeedback: "/feedback",

  // Rerunner Config Routes
  getRerunnerConfigs: "/rerunner/config",
  createRerunnerConfig: "/rerunner/config",
  updateRerunnerConfig: (clientId) => `/rerunner/config/${clientId}`,
  deleteRerunnerConfig: (clientId) => `/rerunner/config/${clientId}`,

  // Credentials Routes
  getCredentials: "/credentials",
  getMidCredentials: (midId) => `/credentials?midId=${midId}`,
  getCredential: (id) => `/credentials/${id}`,
  // DEPRECATED (SOUND-709) — use revealCredential. This GET was an un-audited
  // reveal path until it was fixed server-side; nothing in the app calls it now.
  // Kept only so an older client does not 404 on a reveal, which is the kind of
  // thing people route around.
  getCredentialSecrets: (id) => `/credentials/${id}/secrets`,
  // SOUND-709. setCredentialSecrets is the password field's write path — the
  // endpoint that gives a portal credential somewhere to put a password other
  // than the notes box. revealCredential is audit-logged on every call.
  setCredentialSecrets: (id) => `/credentials/${id}/secrets`,
  revealCredential: (id) => `/credentials/${id}/reveal`,
  attachCredential: (id) => `/credentials/${id}/attach`,
  // SOUND-709 — the DB-backed provider registry, replacing PROVIDER_PRESETS.
  credentialProviders: "/credentials/providers",
  // What a credential can be attached to for one company — DBAs and MIDs, each
  // MID carrying DBA · processor · number so a human can pick the right one.
  credentialLinkTargets: (companyId) => `/credentials/link-targets?companyId=${companyId}`,
  createCredential: "/credentials",
  updateCredential: (id) => `/credentials/${id}`,
  deleteCredential: (id) => `/credentials/${id}`,

  // Pipeline Config Routes
  getPipelineConfigs: "/pipeline-configs",
  getPipelineConfig: (id) => `/pipeline-configs/${id}`,
  createPipelineConfig: "/pipeline-configs",
  updatePipelineConfig: (id) => `/pipeline-configs/${id}`,
  deletePipelineConfig: (id) => `/pipeline-configs/${id}`,

  // Telegram Bot Admin Routes
  getTelegramTemplates: "/telegram/templates",
  getTelegramTemplate: (id) => `/telegram/templates/${id}`,
  createTelegramTemplate: "/telegram/templates",
  updateTelegramTemplate: (id) => `/telegram/templates/${id}`,
  deleteTelegramTemplate: (id) => `/telegram/templates/${id}`,


  getTelegramSubscribers: "/telegram/subscribers",

  // SOUND-247: telegramNotifyBroadcast removed with the endpoint.
  telegramNotifyCompany: (companyId) => `/telegram/notify/company/${companyId}`,
  telegramNotifyApplication: (applicationId) => `/telegram/notify/application/${applicationId}`,

  // Wiki Routes (SOUND-316):
  listWikiArticles: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/wiki/articles${qs ? `?${qs}` : ""}`;
  },
  getWikiArticle: (slug) => `/wiki/articles/${encodeURIComponent(slug)}`,
  listWikiCategories: "/wiki/categories",

  // Billing Routes (SOUND-447) — internal-only, provider-agnostic.
  // Phase B backs these with soundApp.billingInvoices.
  billingInvoices: (provider) => `/billing/${provider}`,
  billingSummary: (provider) => `/billing/${provider}/summary`,
  billingMerchants: (provider) => `/billing/${provider}/merchants`,

  // Ticketing Routes (SOUND-493 / epic SOUND-486) — internal + client +
  // partner, server-scoped, behind requireFeature("ticketing"). The API
  // strips internal-visibility messages/events for non-internal callers.
  listTickets: "tickets",
  ticketStats: "tickets/stats", // SOUND-494: single inbox-KPI aggregate
  createTicket: "tickets",
  ticketRequestTypes: "tickets/request-types",
  getTicketById: (ticketId) => `tickets/${ticketId}`,
  changeTicketStatus: (ticketId) => `tickets/${ticketId}/status`,
  addTicketComment: (ticketId) => `tickets/${ticketId}/comments`,
  changeTicketAssignee: (ticketId) => `tickets/${ticketId}/assignee`,

  // Ticketing Phase 4 (SOUND-526 / backend SOUND-524) — watchers (internal-only).
  // POST body { userId } (omit → self-watch); DELETE by userId.
  addTicketWatcher: (ticketId) => `tickets/${ticketId}/watchers`,
  removeTicketWatcher: (ticketId, userId) => `tickets/${ticketId}/watchers/${userId}`,

  // Ticketing Phase 2 (SOUND-497 / backend SOUND-495) — entity links, typed
  // relations, and single-level nesting. Link/relation/parent writes are
  // internal-only (backend requireRole("internal")). Reverse lookup uses
  // listTickets with ?entityType=&entityId= (scope-enforced).
  addTicketLink: (ticketId) => `tickets/${ticketId}/links`,
  removeTicketLink: (ticketId, linkId) => `tickets/${ticketId}/links/${linkId}`,
  addTicketRelation: (ticketId) => `tickets/${ticketId}/relations`,
  removeTicketRelation: (ticketId, relationId) =>
    `tickets/${ticketId}/relations/${relationId}`,
  setTicketParent: (ticketId) => `tickets/${ticketId}/parent`,
  // SOUND-593 — mint a sub-task directly on an existing ticket. POST body
  // { title, description?, kind?, priority?, assigneeId? }. companyId and
  // parentTicketId are derived server-side from the parent and ignored if sent;
  // priority defaults to the parent's. Distinct from setTicketParent, which
  // ATTACHES an already-existing ticket.
  addTicketChild: (ticketId) => `tickets/${ticketId}/children`,
  ticketsByEntity: (entityType, entityId) =>
    `tickets?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(
      entityId
    )}`,

  // Notification Platform Phase 2 (SOUND-515) — per-user in-app inbox +
  // preferences, all scoped to the caller.
  listNotifications: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/notifications${qs ? `?${qs}` : ""}`;
  },
  notificationUnreadCount: "/notifications/unread-count",
  markNotificationRead: (notificationId) => `/notifications/${notificationId}/read`,
  // SOUND-521 — the reverse. Opening a notification marks it read, so without
  // this a notification clicked in passing is gone as a work signal.
  markNotificationUnread: (notificationId) => `/notifications/${notificationId}/unread`,
  markAllNotificationsRead: "/notifications/read-all",
  notificationPreferences: "/notifications/preferences",

  // SLA Policies (SOUND-534 / backend SOUND-533) — internal-only GLOBAL config
  // CRUD (NOT company-scoped). Powers the /settings SLA policy admin + resolves
  // the policy name for a ticket's stamped sla.policyId. List default = active
  // only; pass { includeInactive: "true" } for all. DELETE soft-deactivates.
  listSlaPolicies: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/sla-policies${qs ? `?${qs}` : ""}`;
  },
  getSlaPolicy: (policyId) => `/sla-policies/${policyId}`,
  createSlaPolicy: "/sla-policies",
  updateSlaPolicy: (policyId) => `/sla-policies/${policyId}`,
  deleteSlaPolicy: (policyId) => `/sla-policies/${policyId}`,

  // CheckoutChamp routing mirror (SOUND-826 Phase 1) — internal-only, READ-ONLY.
  // sound-api snapshots each in-scope client's six CC routing object types
  // (gateways, profiles, cascades, binMaps, binProfiles, recycle) and stores a
  // new snapshot only when something changed. A per-company route 404s for a
  // company with no active CheckoutChamp pipeline config. Refresh is throttled
  // to one per company per 60s (429).
  routingClients: "/routing/clients",
  routingSnapshot: (companyId, snapshotId) =>
    `/routing/clients/${encodeURIComponent(companyId)}/snapshot${
      snapshotId ? `?snapshotId=${encodeURIComponent(snapshotId)}` : ""
    }`,
  routingSnapshots: (companyId) =>
    `/routing/clients/${encodeURIComponent(companyId)}/snapshots`,
  routingRefresh: (companyId) =>
    `/routing/clients/${encodeURIComponent(companyId)}/refresh`,

  // Routing CHANGE SETS (SOUND-827 Phase 2) — propose → tick → GO → verify →
  // roll back. WRITES TO LIVE CHECKOUTCHAMP on apply. Internal-only; apply,
  // decline and rollback are internal admin/super only (the API enforces it).
  routingChangeSets: (companyId, status) =>
    `/routing/clients/${encodeURIComponent(companyId)}/change-sets${
      status ? `?status=${encodeURIComponent(status)}` : ""
    }`,
  routingChangeSet: (changeSetId) => `/routing/change-sets/${encodeURIComponent(changeSetId)}`,
  routingChangeSetApply: (changeSetId) =>
    `/routing/change-sets/${encodeURIComponent(changeSetId)}/apply`,
  routingChangeSetDecline: (changeSetId) =>
    `/routing/change-sets/${encodeURIComponent(changeSetId)}/decline`,
  routingChangeSetRollback: (changeSetId) =>
    `/routing/change-sets/${encodeURIComponent(changeSetId)}/rollback`,

  // Alert coverage (SOUND-847) — internal-only, read-only, over
  // presentation.mart_alert_coverage. Omitted from/to = the API's default (last
  // 30 days to the latest alert_date). `key` is a row key from /alerts
  // ("m:<merchantId>" | "d:<midId>" | "u:<merchantId>").
  alerts: ({ from, to, grain } = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    if (grain) qs.set("grain", grain);
    const s = qs.toString();
    return `/alerts${s ? `?${s}` : ""}`;
  },
  alertSeries: ({ key, from, to }) => {
    const qs = new URLSearchParams({ key });
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    return `/alerts/series?${qs.toString()}`;
  },

  // Notification ADMIN (SOUND-523) — internal-only routing config + channel-
  // agnostic template authoring (telegram/slack/in-app; email stays EJS).
  notificationAdminEvents: "/notification-admin/events",
  notificationAdminEvent: (eventKey) =>
    `/notification-admin/events/${encodeURIComponent(eventKey)}`,
  notificationAdminTemplates: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `/notification-admin/templates${qs ? `?${qs}` : ""}`;
  },
  createNotificationAdminTemplate: "/notification-admin/templates",
  notificationAdminTemplate: (templateId) =>
    `/notification-admin/templates/${templateId}`,
  notificationAdminTemplatePreview: "/notification-admin/templates/preview",
};

export const makeApiRequest = async (
  method,
  url,
  user,
  companyId,
  data,
  optionalConfigs
) => {
  const config = {
    headers: {
      Authorization: user ? `${user.id}.${user.accessToken}` : "",
      CompanyHeader: companyId ? companyId : "",
    },
    ...optionalConfigs,
  };

  switch (method) {
    case "put":
    case "post":
    case "patch":
      return await client[method](url, data, config);
    case "get":
      return await client[method](url, config);
    case "delete":
      // Axios supports a request body on DELETE via config.data (SOUND-294:
      // DELETE /companies/:id/sources/:partnerId accepts a body when removing
      // the last partner).
      return await client.delete(url, { ...config, data });
    default:
      throw new Error("Unsupported API method");
  }
};
